import {
  BoPhan,
  Color,
  DonViTinh,
  DonViTinhPhu,
  GiaBan,
  Group,
  KhoHang,
  KichCo,
  QuocGia,
  Tax,
  ViTriKho,
  VatTuSanPham,
  AccountModel
} from '@/types/schemas';
import { DataTableColumn, SearchSearchColumnDef, MultipleSearchSelect, SelectSearch } from '@/components/custom/erp';
import { Checkbox } from '@/components/ui';

type VatTuTableRow = Omit<VatTuSanPham, 'status'> & {
  ten_dvt?: string;
  ten_lvt?: string;
  tk_khcp?: string;
  status: string | number;
};

export const getItemColumns = (donViTinhs: DonViTinh[] = []): SearchSearchColumnDef<VatTuTableRow>[] => [
  { field: 'ma_vt', headerName: 'Mã vật tư', width: 130 },
  { field: 'ten_vt', headerName: 'Tên vật tư', width: 250 },
  {
    field: 'ten_dvt',
    headerName: 'Đvt',
    width: 90,
    renderCell: params => params.row.dvt_data?.ten_dvt || params.row.ten_dvt,
    renderSearchInput: props => (
      <MultipleSearchSelect
        {...props}
        options={donViTinhs.map(dvt => ({
          value: dvt.dvt,
          label: dvt.ten_dvt
        }))}
      />
    )
  },
  {
    field: 'ma_lvt',
    headerName: 'Loại vật tư',
    width: 170,
    renderCell: params => params.row.ma_lvt_data?.ten_lvt || params.row.ten_lvt
  },
  {
    field: 'ten_nh_vt1',
    headerName: 'Nhóm 1',
    width: 200,
    renderCell: params => params.row.nh_vt1_data?.ten_phan_nhom || params.row.ten_nh_vt1
  },
  {
    field: 'ten_nh_vt2',
    headerName: 'Nhóm 2',
    width: 120,
    renderCell: params => params.row.nh_vt2_data?.ten_phan_nhom || params.row.ten_nh_vt2
  },
  {
    field: 'ten_nh_vt3',
    headerName: 'Nhóm 3',
    width: 120,
    renderCell: params => params.row.nh_vt3_data?.ten_phan_nhom || params.row.ten_nh_vt3
  },
  {
    field: 'lo_yn',
    headerName: 'Theo dõi lô',
    width: 90,
    align: 'center',
    renderCell: params => <Checkbox checked={params.value} disabled />,
    renderSearchInput: ({ value, onChange, onBlur }) => (
      <SelectSearch
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        options={[
          { value: '', label: 'Tất cả' },
          { value: '1', label: 'Chọn' },
          { value: '0', label: 'Không chọn' }
        ]}
        renderOption={(option, isSelected, onSelect) => {
          if (option.value === '') {
            return (
              <div
                className='cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-100'
                onClick={() => onSelect(option.value)}
              >
                {option.label}
              </div>
            );
          }

          return (
            <div
              className='flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-100'
              onClick={() => onSelect(option.value)}
            >
              <Checkbox checked={option.value === '1'} disabled />
              <span>{option.label}</span>
            </div>
          );
        }}
      />
    )
  },
  {
    field: 'qc_yn',
    headerName: 'Quy cách',
    width: 90,
    align: 'center',
    renderCell: params => <Checkbox checked={params.value} disabled />,
    renderSearchInput: ({ value, onChange, onBlur }) => (
      <SelectSearch
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        options={[
          { value: '', label: 'Tất cả' },
          { value: '1', label: 'Chọn' },
          { value: '0', label: 'Không chọn' }
        ]}
        renderOption={(option, isSelected, onSelect) => {
          if (option.value === '') {
            return (
              <div
                className='cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-100'
                onClick={() => onSelect(option.value)}
              >
                {option.label}
              </div>
            );
          }

          return (
            <div
              className='flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-100'
              onClick={() => onSelect(option.value)}
            >
              <Checkbox checked={option.value === '1'} disabled />
              <span>{option.label}</span>
            </div>
          );
        }}
      />
    )
  },
  {
    field: 'tk_vt',
    headerName: 'Tk kho',
    width: 90,
    renderCell: params => params.row.tk_khcp_data?.code || params.row.tk_vt || params.row.tk_khcp
  },
  {
    field: 'tk_dt',
    headerName: 'Tk doanh thu',
    width: 100,
    renderCell: params => params.row.tk_dt_data?.code || params.row.tk_dt
  },
  {
    field: 'tk_gv',
    headerName: 'Tk giá vốn',
    width: 90,
    renderCell: params => params.row.tk_gv_data?.code || params.row.tk_gv
  },
  { field: 'image_id', headerName: 'Hình ảnh', width: 100 },
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 140,
    renderCell: params => {
      const status = params.row.status;
      switch (status) {
        case '0':
          return '0. Không sử dụng';
        case '1':
          return '1. Còn Sử dụng';
        default:
          return '2. Quy cách chi tiết';
      }
    },
    renderSearchInput: ({ value, onChange, onBlur }) => (
      <MultipleSearchSelect
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        options={[
          { value: '0', label: '0. Không sử dụng' },
          { value: '1', label: '1. Còn Sử dụng' },
          { value: '2', label: '2. Quy cách chi tiết' }
        ]}
      />
    )
  }
];

// Unit conversion table columns
export const unitConversionColumns: DataTableColumn<DonViTinhPhu>[] = [
  { field: 'ma_dvt', headerName: 'Đvt', width: 100 },
  { field: 'he_so', headerName: 'Hệ số', width: 100, type: 'number' }
];

// Price list table columns
export const priceListColumns: DataTableColumn<GiaBan>[] = [
  {
    field: 'effective_date',
    headerName: 'Ngày hiệu lực',
    width: 130,
    type: 'date'
  },
  { field: 'price', headerName: 'Giá bán', width: 120, type: 'number' },
  { field: 'customer_code', headerName: 'Mã khách hàng', width: 150 }
];

// Product code table columns
export const productCodeColumns: DataTableColumn<Record<string, unknown>>[] = [
  { field: 'product_code', headerName: 'Mã hàng hoá', width: 150 }
];

export const uomSearchColumns: DataTableColumn<DonViTinh>[] = [
  { field: 'dvt', headerName: 'Đvt', width: 150 },
  { field: 'ten_dvt', headerName: 'Tên đơn vị tính', width: 200 }
];

export const itemGroupSearchColumns: DataTableColumn<Group>[] = [
  { field: 'ma_nhom', headerName: 'Mã nhóm', width: 150 },
  { field: 'ten_phan_nhom', headerName: 'Tên nhóm', width: 200 }
];

export const KhoHangSearchColBasicInfo: DataTableColumn<KhoHang>[] = [
  { field: 'ma_kho', headerName: 'Mã kho', flex: 1 },
  { field: 'ten_kho', headerName: 'Tên kho', flex: 2 },
  { field: 'don_vi', headerName: 'Đơn vị', flex: 1 },
  { field: 'theo_doi_vi_tri', headerName: 'Theo dõi vị trí', flex: 1 }
];

export const viTriSearchColBasicInfo: DataTableColumn<ViTriKho>[] = [
  { field: 'ma_vi_tri', headerName: 'Mã vị trí', flex: 1 },
  { field: 'ten_vi_tri', headerName: 'Tên vị trí', flex: 2 }
];

export const thueSearchColBasicInfo: DataTableColumn<Tax>[] = [
  { field: 'ma_thue', headerName: 'Mã thuế', flex: 1 },
  { field: 'ten_thue', headerName: 'Tên thuế', flex: 2 }
];

export const boPhanSearchColumns: DataTableColumn<BoPhan>[] = [
  { field: 'ma_bp', headerName: 'Mã bộ phận', flex: 1 },
  { field: 'ten_bp', headerName: 'Tên bộ phận', flex: 2 }
];

export const DonViSearchColBasicInfo: DataTableColumn<Record<string, unknown>>[] = [
  { field: 'ma_don_vi', headerName: 'Mã đơn vị', flex: 1 },
  { field: 'ten_don_vi', headerName: 'Tên đơn vị', flex: 2 },
  { field: 'id', headerName: 'ID', flex: 1 }
];

export const quocGiaSearchColumns: DataTableColumn<QuocGia>[] = [
  {
    field: 'checkbox',
    flex: 1,
    width: 50,
    renderHeader: () => <Checkbox onChange={() => {}} />
  },
  { field: 'ma_qg', headerName: 'Mã quốc gia', flex: 1 },
  { field: 'ten_qg', headerName: 'Tên quốc gia', flex: 2 }
];

export const mauSacSearchColumns: DataTableColumn<Color>[] = [
  {
    field: 'checkbox',
    flex: 1,
    width: 50,
    renderHeader: () => <Checkbox onChange={() => {}} />
  },
  { field: 'ma_mau_sac', headerName: 'Mã màu sắc', flex: 1 },
  { field: 'ten_mau_sac', headerName: 'Tên màu sắc', flex: 2 },
  { field: 'mau', headerName: 'Màu', flex: 2 }
];

export const kichCoSearchColumns: DataTableColumn<KichCo>[] = [
  {
    field: 'checkbox',
    flex: 1,
    width: 50,
    renderHeader: () => <Checkbox onChange={() => {}} />
  },
  { field: 'ma_kich_co', headerName: 'Mã kích cỡ', flex: 1 },
  { field: 'ten_kich_co', headerName: 'Tên kích cỡ', flex: 2 }
];

export const ConstraintColumns: DataTableColumn<Record<string, unknown>>[] = [
  { field: 'ten_doi_tuong', headerName: 'Tên đối tượng', width: 100 },
  {
    field: 'bat_buoc_nhap',
    headerName: 'Bắt buộc nhập',
    width: 120,
    renderCell: params => <Checkbox checked={params.value} />
  }
];

export const accountSearchColumns: SearchSearchColumnDef<AccountModel>[] = [
  { field: 'id', headerName: 'Mã tài khoản', flex: 1 },
  { field: 'name', headerName: 'Tên tài khoản', flex: 1 },
  { field: 'parent_account_code', headerName: 'Tài khoản mẹ', flex: 1 },
  {
    field: 'tk_so_cai',
    headerName: 'Tk sổ cái',
    flex: 1,
    checkboxSelection: true
  },
  {
    field: 'tk_chi_tiet',
    headerName: 'Tk chi tiết',
    flex: 1,
    checkboxSelection: true
  },
  { field: 'bac_tk', headerName: 'Bậc tk', flex: 1 }
];

export const loaiVatTuOptions = [
  { value: '0', label: '00. Dịch vụ' },
  { value: '21', label: '21. Vật tư' },
  { value: '22', label: '22. Phụ tùng' },
  { value: '31', label: '31. CCLĐ' },
  { value: '41', label: '41. Bán thành phẩm' },
  { value: '51', label: '51. Thành phẩm' },
  { value: '61', label: '61. Hàng hóa' },
  { value: '71', label: '71. Hàng gia công' }
];

export const cachTinhGiaTonKhoOptions = [
  { value: 0, label: '0. Theo khai báo đơn vị' },
  { value: 1, label: '1. Giá trung bình' },
  { value: 2, label: '2. Nhập trước xuất trước' },
  { value: 3, label: '3. Giá đích danh' },
  { value: 4, label: '4. Giá trung bình di động' }
];
